import useMinimumLoading from "../../hooks/useMinimumLoading";
import GroupSkeleton from "../../components/loading/GroupSkeleton";
import React, {useEffect, useState} from "react";
import {motion} from "motion/react";

import BGirlMimoji from "../../assets/images/BGirlMimoji.png";
import GirlMimoji from "../../assets/images/GirlMimoji.png";
import SunglassMimoji from "../../assets/images/SunglassMimoji.png";
import {LeftFriendsLine} from "../../assets/svgComponents/inviteFriendsPage/LeftFriendsLine";
import {RightFriendsLine} from "../../assets/svgComponents/inviteFriendsPage/RightFriendsLine";
import {PlaneIcon} from "../../assets/svgComponents/PlaneIcon";
import {SmallRemoveIcon} from "../../assets/svgComponents/SmallRemoveIcon";
import {DecorationUnderline} from "../../assets/svgComponents/inviteFriendsPage/DecorationUnderline";
import {GroupSessionService} from "../../services/GroupSessionService";
import {useNavigate} from "react-router-dom";
import {FarewiseIconLight} from "../../assets/svgComponents/FarewiseIconLight";

// ======================= CityDropdown (as provided) =======================
import GroupAirportInput from "../../components/input/GroupAirportInput";
import {AirportService} from "../../services/AirportService";
import {Airport} from "../../models/flightSearchData";
import {PenIcon} from "../../assets/svgComponents/PenIcon";

interface CityDropdownProps {
    airports: Airport[];
    selectedCode: string;
    onSelect: (code: string) => void;
}

export function CityDropdown({selectedCode, onSelect, airports}: CityDropdownProps) {
    return <div className="relative w-[241px]">
        <GroupAirportInput compact label="Departure airport"
            airports={airports.map(airport => ({iata: airport.code, city: airport.city}))}
            selected={selectedCode ? [selectedCode] : []}
            onChange={codes => onSelect(codes[0] || '')}/>
    </div>;
}

export function NameInput({value, onChange}: { value: string; onChange: (v: string) => void }) {
    return (
        <div className="relative w-full max-w-xl">
            {/* Icon */}
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
            <PenIcon/>
          </span>

            <span
                className="pointer-events-none absolute left-[44px] top-1/2 -translate-y-1/2 h-6 w-px bg-[rgba(178,130,229,0.25)]"/>

            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Titel eintragen"
                className="
              block h-12 w-full rounded-[10px] border border-[rgba(178,130,229,0.2)] bg-white
              pl-[60px] pr-4 py-3 text-[14.5px] font-semibold text-[#3e3e3e] outline-none
              placeholder:text-[#c9c9c9]
              focus:ring-2 focus:ring-[#b282e5]/40
            "
                aria-label="Session name"
            />
        </div>
    );
}


export default function SessionCreatePage() {
    const navigate = useNavigate();

    const [selectedName, setSelectedName] = useState<string>("");
    const [selectedAirportCode, setSelectedAirportCode] = useState<string>("BER");

    const [selectedAirports, setSelectedAirports] = useState<string[]>([]); // Codes


    const [loading, setLoading] = useState(false);
    const [airportsLoading, setAirportsLoading] = useState(true);
    const [airports, setAirports] = useState<Airport[]>([]);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        let active = true;
        AirportService.getAirports().then(items => {
            if (active) setAirports(items.map(a => ({code: a.iata, city: a.city})));
        }).catch(() => { if (active) setError("Could not load airports. Please reload to try again."); }).finally(() => { if (active) setAirportsLoading(false); });
        return () => { active = false; };
    }, []);


    // Derived flags
    const canAddAirport =
        !!selectedAirportCode &&
        !selectedAirports.includes(selectedAirportCode) &&
        selectedAirports.length === 0;

    // Add/remove airports to list (tags)
    const handleAddAirport = () => {
        if (!canAddAirport) return;
        setSelectedAirports([selectedAirportCode]);
    };

    const handleRemoveAirport = (codeToRemove: string) => {
        setSelectedAirports(prev => prev.filter(c => c !== codeToRemove));
    };

    // Create session with name + departure airports (adjust service as needed)
    const handleCreateSession = async () => {
        if (loading || !selectedName.trim() || selectedAirports.length !== 1) return;
        setError(null);
        try {
            setLoading(true);
            const response = await GroupSessionService.createSession(
                selectedName.trim(),
                selectedAirports[0] ?? selectedAirportCode,
                []
            );
            navigate(`/sessions/${response.sessionId}/invite`);
        } catch (err) {
            setError("Could not create the session. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    const showAirportsLoading = useMinimumLoading(airportsLoading);

    return (
        <motion.div
            className="bg-gradient-to-b from-[#f6f8ff] from-[43.912%] to-[#dde5ff] relative h-screen w-full"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.25, ease: "easeOut"}}
        >
            {error && <p role="alert" className="absolute top-6 inset-x-6 text-center text-red-600">{error}</p>}
            {/* Top-left: back */}
            <button
                type="button"
                aria-label="Back"
                className="absolute left-28 top-20 z-50 hover:scale-105"
                onClick={() => (navigate(-1))}
            >
                <FarewiseIconLight/>
            </button>

            {/* Content */}
            <div className="flex h-full w-full items-center justify-center relative">
                <div
                    className="box-border flex h-full w-full flex-col items-center justify-center gap-2.5 px-[72px] relative">
                    <div className="content-stretch flex items-center justify-center relative w-full">
                        <div
                            className="content-stretch flex flex-col items-start justify-start gap-[47px] relative w-[957.5px]">
                            {/* Headline */}
                            <div
                                className="content-stretch flex items-center justify-start gap-[35px] leading-[0] relative w-full">
                                <div
                                    className="content-stretch flex flex-col items-start justify-start gap-[34px] h-[143.333px] relative w-[490.833px]">
                                    <div
                                        className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative">
                                        <motion.div
                                            className="[grid-area:1_/_1] h-[4.396px] ml-[262.099px] mt-[52.75px] relative w-[185.172px]"
                                            initial={{opacity: 0, scaleX: 0}}
                                            animate={{opacity: 1, scaleX: 1}}
                                            transition={{duration: 0.4, delay: 0.3, ease: "easeOut"}}
                                        >
                                            <div className="absolute inset-[-187.5%_-4.45%_-187.51%_-4.45%]">
                                                <DecorationUnderline/>
                                            </div>
                                        </motion.div>

                                        <motion.div
                                            className="[grid-area:1_/_1] font-outfit font-semibold leading-[0] ml-0 mt-0 not-italic relative text-[#232323] text-[54.947px] text-nowrap tracking-[-1.2088px]"
                                            initial={{opacity: 0, y: -30}}
                                            animate={{opacity: 1, y: 0}}
                                            transition={{duration: 0.25, delay: 0.1, ease: "easeOut"}}
                                        >
                                            <p className="leading-[normal] whitespace-pre">
                                                Set up your session.
                                            </p>
                                        </motion.div>
                                    </div>

                                    <motion.p
                                        className="basis-0 grow min-h-px min-w-px font-outfit font-medium text-[#858ca7] text-[16.667px] tracking-[-0.3667px] w-full leading-normal"
                                        initial={{opacity: 0, y: -20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.2, delay: 0.2, ease: "easeOut"}}
                                    >
                                        Pick a memorable name and choose your departure airport(s).
                                    </motion.p>
                                </div>

                                {/* Right visuals (restored: frosted plates, emojis, bubbles) */}
                                <motion.div
                                    className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative shrink-0 scale-90"
                                    initial={{opacity: 0, scale: 0.81}}
                                    animate={{opacity: 1, scale: 0.9}}
                                    transition={{duration: 0.3, delay: 0.4, ease: "easeOut"}}
                                >
                                    {/* --- Frosted plates behind avatars (exact positions from original) --- */}
                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[101.545px] items-center justify-center ml-[260.001px] mt-[55.833px] relative w-[101.545px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.6, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[353.427deg]">
                                            <div
                                                className="pointer-events-none relative size-[91.667px] rounded-[25px] bg-[rgba(255,255,255,0.5)]">
                                                <div
                                                    aria-hidden="true"
                                                    className="absolute inset-0 rounded-[25px] border border-[rgba(255,255,255,0.5)] shadow-[0_0_13.333px_0_rgba(0,0,0,0.09)]"
                                                />
                                                <div
                                                    className="absolute inset-0 shadow-[0_0_10px_0_inset_rgba(255,255,255,0.8)]"/>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[95.953px] items-center justify-center ml-[180px] mt-[19.167px] relative w-[95.953px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.5, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[2.753deg]">
                                            <div
                                                className="pointer-events-none relative size-[91.667px] rounded-[25px] bg-[rgba(255,255,255,0.5)]">
                                                <div
                                                    aria-hidden="true"
                                                    className="absolute inset-0 rounded-[25px] border border-[rgba(255,255,255,0.5)] shadow-[0_0_13.333px_0_rgba(0,0,0,0.09)]"
                                                />
                                                <div
                                                    className="absolute inset-0 shadow-[0_0_10px_0_inset_rgba(255,255,255,0.8)]"/>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[104.247px] items-center justify-center ml-[97.501px] mt-[52.871px] relative w-[104.247px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.45, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[351.463deg]">
                                            <div
                                                className="pointer-events-none relative size-[91.667px] rounded-[25px] bg-[rgba(255,255,255,0.5)]">
                                                <div
                                                    aria-hidden="true"
                                                    className="absolute inset-0 rounded-[25px] border border-[rgba(255,255,255,0.5)] shadow-[0_0_13.333px_0_rgba(0,0,0,0.09)]"
                                                />
                                                <div
                                                    className="absolute inset-0 shadow-[0_0_10px_0_inset_rgba(255,255,255,0.8)]"/>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* --- Avatars (Mimojis) --- */}
                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[74.783px] items-center justify-center ml-[274.135px] mt-[72.07px] relative w-[74.783px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.65, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[353.427deg]">
                                            <div
                                                className="size-[67.5px] bg-cover bg-center bg-no-repeat"
                                                style={{backgroundImage: `url('${SunglassMimoji}')`}}
                                                aria-label="avatar-1"
                                            />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[76.766px] items-center justify-center ml-[189.477px] mt-[31.26px] relative w-[76.766px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.55, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[2.753deg]">
                                            <div
                                                className="size-[73.333px] bg-cover bg-center bg-no-repeat"
                                                style={{backgroundImage: `url('${BGirlMimoji}')`}}
                                                aria-label="avatar-2"
                                            />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[73.621px] items-center justify-center ml-[112.819px] mt-[69.856px] relative w-[73.621px]"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        transition={{duration: 0.2, delay: 0.5, ease: "easeOut"}}
                                    >
                                        <div className="flex-none rotate-[351.784deg]">
                                            <div
                                                className="size-[65px] bg-cover bg-center bg-no-repeat"
                                                style={{backgroundImage: `url('${GirlMimoji}')`}}
                                                aria-label="avatar-3"
                                            />
                                        </div>
                                    </motion.div>

                                    {/* --- Emoji decorations --- */}
                                    <div
                                        className="[grid-area:1_/_1] flex h-[24.622px] items-center justify-center ml-[344.167px] mt-[121.568px] relative w-[35.361px]">
                                        <div className="flex-none rotate-[351.503deg]">
                                            <p className="font-['Outfit:Medium',_sans-serif] text-[33.333px] leading-[20px] tracking-[-0.7333px]">🥳</p>
                                        </div>
                                    </div>

                                    <div
                                        className="[grid-area:1_/_1] flex h-[24.556px] items-center justify-center ml-[87.501px] mt-[128.236px] relative w-[35.328px]">
                                        <div className="flex-none rotate-[351.631deg]">
                                            <p className="font-['Outfit:Medium',_sans-serif] text-[33.333px] leading-[20px] tracking-[-0.7333px]">🏛️</p>
                                        </div>
                                    </div>

                                    {/* --- Chat bubbles --- */}
                                    {/* Left purple bubble */}
                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[39.167px] items-center justify-center ml-0 mt-0 relative w-[201.667px]"
                                        initial={{opacity: 0, x: -30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.7, ease: "easeOut"}}
                                    >
                                        <div className="size-full h-[39.167px] w-[201.667px] rotate-180">
                                            <div
                                                className="size-full rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] bg-[#b282e5]">
                                                <div
                                                    aria-hidden="true"
                                                    className="pointer-events-none absolute inset-0 rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] border-[1.083px] border-[rgba(116,65,255,0.24)]"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                    <motion.div
                                        className="[grid-area:1_/_1] font-['Outfit',_sans-serif] font-medium leading-[0] ml-[18.334px] mt-2.5 not-italic relative text-[14.167px] text-nowrap text-white"
                                        initial={{opacity: 0, x: -30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.75, ease: "easeOut"}}
                                    >
                                        <p className="leading-[normal] whitespace-pre">We need a beach nearby!</p>
                                    </motion.div>

                                    {/* Right blue bubble */}
                                    <motion.div
                                        className="[grid-area:1_/_1] flex h-[39.167px] items-center justify-center ml-[335.834px] mt-[37.5px] relative w-[95.833px]"
                                        initial={{opacity: 0, x: 30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.8, ease: "easeOut"}}
                                    >
                                        <div className="size-full h-[39.167px] w-[95.833px] scale-y-[-100%]">
                                            <div
                                                className="size-full rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] bg-[#7ca8ff]">
                                                <div
                                                    aria-hidden="true"
                                                    className="pointer-events-none absolute inset-0 rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] border-[1.083px] border-[#5f95ff]"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                    <motion.div
                                        className="[grid-area:1_/_1] font-['Outfit',_sans-serif] font-medium leading-[0] ml-[354.167px] mt-[48.333px] not-italic relative text-[14.167px] text-nowrap text-white"
                                        initial={{opacity: 0, x: 30}}
                                        animate={{opacity: 1, x: 0}}
                                        transition={{duration: 0.2, delay: 0.85, ease: "easeOut"}}
                                    >
                                        <p className="leading-[normal] whitespace-pre">{`& music!`}</p>
                                    </motion.div>

                                    {/* Small dark bubble with ellipsis */}
                                    <motion.div
                                        className="[grid-area:1_/_1] bg-[#1e3f80] h-[39.167px] ml-[128.334px] mt-[148.333px] relative w-[96.667px] rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px]"
                                        initial={{opacity: 0, y: 20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.2, delay: 0.9, ease: "easeOut"}}
                                    >
                                        <div
                                            aria-hidden="true"
                                            className="pointer-events-none absolute inset-0 rounded-bl-[16.667px] rounded-br-[16.667px] rounded-tl-[8.333px] rounded-tr-[16.667px] border-[1.083px] border-[rgba(255,255,255,0.24)]"
                                        />
                                    </motion.div>
                                    <motion.div
                                        className="[grid-area:1_/_1] font-['Outfit',_sans-serif] font-normal leading-[0] ml-[167.501px] mt-[150px] not-italic relative text-[20px] text-nowrap text-white"
                                        initial={{opacity: 0, y: 20}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.2, delay: 0.95, ease: "easeOut"}}
                                    >
                                        <p className="leading-[normal] whitespace-pre">...</p>
                                    </motion.div>
                                </motion.div>
                            </div>

                            {/* Options A/B */}
                            <div
                                className="content-stretch flex items-center justify-start gap-[35px] relative w-[900px]">
                                {/* Option A – Select Name (replaces Share Link) */}
                                <motion.div
                                    className="content-stretch z-40 flex w-[369.167px] flex-col items-start justify-start gap-[15px] leading-[0] relative"
                                    initial={{opacity: 0, y: 30}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.25, delay: 0.8, ease: "easeOut"}}
                                >
                                    <motion.p
                                        className="font-outfit font-medium text-[rgba(133,140,167,0.27)] text-[16.667px] tracking-[-0.3667px]"
                                        initial={{opacity: 0, y: -10}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.15, delay: 0.85, ease: "easeOut"}}
                                    >
                                        Option A
                                    </motion.p>

                                    <div className="relative w-[369.167px] h-[223.333px] rounded-[19.224px]
                bg-white/50 backdrop-blur border border-white/50
                shadow-[0_0_13.333px_rgba(0,0,0,0.07)] p-[33.334px]">
                                        <div
                                            aria-hidden
                                            className="pointer-events-none absolute inset-0 rounded-[19.224px]
               shadow-[0_0_5.492px_inset_rgba(255,255,255,0.8)]"
                                        />

                                        <div className="relative flex h-full pt-3 flex-col z-10">
                                            {/* Title */}
                                            <div
                                                className="font-outfit font-semibold text-[#232323] text-[18.333px] tracking-[-0.4033px]">
                                                A Catchy Name for your Trip
                                            </div>

                                            {/* Input */}
                                            <div className="mt-8">
                                                <NameInput value={selectedName} onChange={setSelectedName}/>
                                            </div>

                                            {/* Helper text (am unteren Rand) */}
                                            <div
                                                className="mt-auto leading-tight font-outfit font-medium text-[#bdc1d2] text-[13.333px] tracking-[-0.2933px]">
                                                Give your session a friendly name everyone recognizes.
                                            </div>
                                        </div>
                                    </div>

                                </motion.div>

                                {/* Option B – Departure Airport(s) via CityDropdown (replaces Email) */}
                                <motion.div
                                    className="content-stretch flex w-[495.833px] flex-col items-start justify-start gap-[15px] leading-[0] relative"
                                    initial={{opacity: 0, y: 30}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.25, delay: 0.9, ease: "easeOut"}}
                                >
                                    <motion.p
                                        className="font-outfit font-medium text-[rgba(133,140,167,0.27)] text-[16.667px] tracking-[-0.3667px]"
                                        initial={{opacity: 0, y: -10}}
                                        animate={{opacity: 1, y: 0}}
                                        transition={{duration: 0.15, delay: 0.95, ease: "easeOut"}}
                                    >
                                        Option B
                                    </motion.p>

                                    <div
                                        className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative">
                                        {/* Card BG */}
                                        <div
                                            className="[grid-area:1_/_1] pointer-events-none relative h-[223.333px] w-[495.833px] rounded-[19.224px] bg-[rgba(255,255,255,0.5)]">
                                            <div
                                                aria-hidden
                                                className="absolute inset-0 rounded-[19.224px] border-[0.915px] border-[rgba(255,255,255,0.5)] border-solid shadow-[0px_0px_13.333px_0px_rgba(0,0,0,0.07)]"
                                            />
                                            <div
                                                className="absolute inset-0 shadow-[0px_0px_5.492px_0px_inset_rgba(255,255,255,0.8)]"/>
                                        </div>

                                        {/* Title */}
                                        <div
                                            className="[grid-area:1_/_1] font-outfit font-semibold leading-[0] ml-[33.333px] mt-[37.5px] not-italic relative text-[#232323] text-[18.333px] tracking-[-0.4033px]">
                                            <p className="leading-[20px] whitespace-pre">Departure Airport</p>
                                        </div>

                                        {/* Add Button */}
                                        <motion.div
                                            className="[grid-area:1_/_1] h-[49.167px] w-[92.5px] ml-[354.167px] mt-[84.167px] rounded-[15px] z-20 cursor-pointer outline-none focus:outline-none"
                                            onClick={canAddAirport ? handleAddAirport : undefined}
                                            whileHover={
                                                canAddAirport
                                                    ? {backgroundColor: "#9a6bc7", transition: {duration: 0.2}}
                                                    : {}
                                            }
                                            whileTap={canAddAirport ? {scale: 0.98, transition: {duration: 0.1}} : {}}
                                            style={{
                                                backgroundColor: canAddAirport ? "#b282e5" : "rgba(178,130,229,0.7)",
                                            }}
                                            aria-label="Add departure"
                                        />

                                        {/* Tags for selected airports (max 2 visible like original) */}
                                        {selectedAirports.slice(0, 2).map((city, index) => {
                                            const textWidth = city.length * 6.5;
                                            const tagWidth = Math.max(textWidth + 40, 85);
                                            const firstWidth = Math.max(
                                                (selectedAirports[0] || "").length * 6.5 + 40,
                                                85
                                            );
                                            const left = index === 0 ? 33.333 : 33.333 + firstWidth + 12;
                                            return (
                                                <div
                                                    key={city}
                                                    className="[grid-area:1_/_1] mt-[142.5px] flex h-[31.053px] items-center justify-between rounded-[7.763px] bg-[rgba(178,130,229,0.13)] px-[12px] relative"
                                                    style={{marginLeft: `${left}px`, width: `${tagWidth}px`}}
                                                >
                                                    <div
                                                        className="font-outfit font-medium text-[#b282e5] text-[10.868px] pr-2">
                                                        <p className="leading-[normal] whitespace-pre">{city}</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity duration-150"
                                                        onClick={() => handleRemoveAirport(city)}
                                                        aria-label={`Remove ${city}`}
                                                    >
                                                        <SmallRemoveIcon/>
                                                    </button>
                                                </div>
                                            );
                                        })}

                                        {/* +N Indicator */}
                                        {selectedAirports.length > 2 &&
                                            (() => {
                                                const firstW = Math.max(
                                                    (selectedAirports[0] || "").length * 6.5 + 40,
                                                    85
                                                );
                                                const secondW = selectedAirports[1]
                                                    ? Math.max(selectedAirports[1].length * 6.5 + 40, 85)
                                                    : 0;
                                                const left = 33.333 + firstW + 12 + secondW + 12;
                                                return (
                                                    <div
                                                        className="[grid-area:1_/_1] mt-[142.5px] flex h-[31.053px] items-center justify-center rounded-[7.763px] bg-[rgba(178,130,229,0.13)] px-[12px] relative"
                                                        style={{marginLeft: `${left}px`, width: "50px"}}
                                                    >
                                                        <div
                                                            className="font-outfit font-medium text-[#b282e5] text-[10.868px]">
                                                            <p className="leading-[normal] whitespace-pre">
                                                                +{selectedAirports.length - 2}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                        {/* Input area replaced with CityDropdown */}
                                        <div
                                            className="[grid-area:1_/_1] bg-white h-[50px] w-[309.167px] ml-[33.334px] mt-[83.333px] relative rounded-[10px]">
                                            <div
                                                aria-hidden
                                                className="pointer-events-none absolute inset-0 rounded-[10px] border-[1.41px] border-[rgba(178,130,229,0.2)] border-solid"
                                            />

                                            {/* CityDropdown positioned inside the card */}
                                            <div className="absolute left-[30px] top-[12px]">
                                                <div className="w-[241px]">{showAirportsLoading ? <GroupSkeleton variant="field" label="Flughäfen werden geladen"/> : <CityDropdown
                                                    airports={airports}
                                                    selectedCode={selectedAirportCode}
                                                    onSelect={setSelectedAirportCode}
                                                />}</div>
                                            </div>
                                        </div>

                                        {/* Add Button Text */}
                                        <div
                                            className="[grid-area:1_/_1] z-30 pointer-events-none font-['Space_Grotesk',_sans-serif] font-bold text-[#f6f8ff] text-[15.978px] text-center tracking-[-0.8788px] ml-[400.333px] mt-[98.333px] translate-x-[-50%]">
                                            <p className="leading-[normal] whitespace-pre">Add</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>

                            {/* CTA */}
                            <motion.div
                                className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start relative shrink-0 ml-[70px] leading-[0]"
                                initial={{opacity: 0, y: 30}}
                                animate={{opacity: 1, y: 0}}
                                transition={{
                                    opacity: {duration: 0.3, delay: 1.1, ease: "easeOut"},
                                    y: {duration: 0.3, delay: 1.1, ease: "easeOut"},
                                }}
                            >
                                <motion.button
                                    type="button"
                                    className="content-stretch flex items-center justify-start gap-[13.696px] w-[198.333px] cursor-pointer rounded-[26.25px] bg-[#b282e5] pl-[29.167px] pr-[26.667px] py-[15.978px] relative ml-[180.833px] mt-[19.167px] box-border disabled:opacity-60"
                                    whileHover={{scale: 1.05, boxShadow: "0 8px 25px rgba(178,130,229,0.4)"}}
                                    whileTap={{scale: 0.98}}
                                    onClick={handleCreateSession}
                                    disabled={loading || !selectedName.trim() || selectedAirports.length === 0}
                                    aria-label="Create Session"
                                >
                                    <PlaneIcon/>
                                    <span
                                        className="font-['Space_Grotesk',_sans-serif] font-black text-[#f6f8ff] text-[15.978px] tracking-[-0.8788px]">
                    {loading ? "Creating..." : "Create Session"}
                  </span>
                                </motion.button>

                                {/* Left friends line (absolute, stable pos) */}
                                <motion.div
                                    className="[grid-area:1_/_1] absolute left-0 top-0 h-[50px] w-[123.333px] z-20 pointer-events-none"
                                    initial={{opacity: 0, x: -20}}
                                    animate={{opacity: 1, x: 0}}
                                    transition={{duration: 0.2, delay: 1.3, ease: 'easeOut'}}
                                >
                                    <LeftFriendsLine/>
                                </motion.div>

                                {/* Right friends line (fixed the ml typo -> use absolute left) */}
                                <motion.div
                                    className="[grid-area:1_/_1] absolute left-[431.667px] top-0 h-[50px] w-[122.5px] z-20 pointer-events-none"
                                    initial={{opacity: 0, x: 20}}
                                    animate={{opacity: 1, x: 0}}
                                    transition={{duration: 0.2, delay: 1.35, ease: 'easeOut'}}
                                >
                                    <RightFriendsLine/>
                                </motion.div>

                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
